import { Skeleton } from '@/components/ui/skeleton';
import { PageWrapper, Container } from '@/components/layout';

/**
 * Skeleton for the Bill Details page.
 * Reserves ~640px total height to prevent CLS.
 *
 * Layout breakdown:
 * - Custom header with bill info: ~200px
 * - Version timeline (optional): ~100px
 * - Tabs: ~40px
 * - Tab content (summary): ~300px
 */
export function BillDetailsPageSkeleton() {
  return (
    <PageWrapper customContainer={true}>
      {/* Custom Bill Header */}
      <header className="w-full relative overflow-hidden border-b border-border">
        <div
          className="absolute inset-y-0 w-[100vw] left-1/2 -translate-x-1/2 bg-gradient-to-r from-background via-background to-muted/30 dark:from-dark-base dark:via-dark-base dark:to-dark-elevated/50"
          aria-hidden="true"
        />
        <Container size="xl" padding className="relative z-10">
          <div className="py-4 lg:py-6">
            {/* Back button */}
            <Skeleton className="h-6 w-24 mb-3" />

            {/* Bill number and status */}
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-6 w-24 rounded-full" />
              <Skeleton className="h-6 w-20 rounded-full" />
            </div>

            {/* Bill title */}
            <Skeleton className="h-9 w-full max-w-2xl mb-2" />
            <Skeleton className="h-9 w-3/4 max-w-xl mb-4" />

            {/* Sponsor info */}
            <div className="flex items-center gap-3 mb-4">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div>
                <Skeleton className="h-4 w-32 mb-1" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2">
              <Skeleton className="h-10 w-28" />
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-20" />
            </div>
          </div>
        </Container>
      </header>

      <Container size="xl" padding>
        {/* Version Timeline */}
        <div className="py-4 border-b border-border">
          <div className="flex items-center gap-4 overflow-x-auto">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border mt-4">
          <div className="flex gap-4 -mb-px">
            {['Summary', 'Analysis', 'Documents', 'Testimony', 'Votes', 'Activity'].map((tab) => (
              <Skeleton key={tab} className="h-10 w-20" />
            ))}
          </div>
        </div>

        {/* Tab Content - Summary */}
        <div className="py-6 space-y-6">
          {/* Headline summary */}
          <div className="bg-muted/30 rounded-lg p-4">
            <Skeleton className="h-5 w-32 mb-3" />
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-3/4" />
          </div>

          {/* Full summary */}
          <div className="space-y-3">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
          </div>

          {/* Key provisions */}
          <div className="space-y-3">
            <Skeleton className="h-6 w-36" />
            <div className="space-y-2 pl-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Skeleton className="h-4 w-4 rounded-full flex-shrink-0 mt-0.5" />
                  <Skeleton className="h-4 flex-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </PageWrapper>
  );
}
